import { useState, useEffect, useContext, useMemo } from 'react'
import { TextField, Box } from '@mui/material'
import { fetchData } from '../../../global/utils/fetch'
import {
  getDefectsDashboardData,
  getDefectsDashboardByTechnician,
  getDefectsDashboardByLocation,
} from './api'
import {
  DefectsDashboardData,
  DefectsByTechnicianData,
  DefectsByLocationData,
  DefectTypeData,
} from '../../../../../app/types/defects-dashboard'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import Dashboard, {
  DashboardColumn,
} from '../../../global/components/dashboard/Dashboard'
import MultiLineDashboard, {
  MultiLineDashboardColumn,
  AggregateEntry,
} from '../../../global/components/dashboard/MultiLineDashboard'
import formatCurrency from '../../../global/utils/format-currency-usd'
import { formatDateYyyyMmDd } from '../../../global/utils/date/date-utils-yyyy-mm-dd'
import PrimaryCheckbox from '../../../global/components/checkbox/Checkbox'

type ViewMode =
  | 'default'
  | 'by-technician'
  | 'by-location'
  | 'at-fault-by-technician'

// Default to current month
function getDefaultStartDate(): string {
  const date = new Date()
  date.setDate(1) // First day of current month
  return formatDateYyyyMmDd(date)
}

function getDefaultEndDate(): string {
  const date = new Date()
  date.setMonth(date.getMonth() + 1) // Next month
  date.setDate(0) // Last day of current month
  return formatDateYyyyMmDd(date)
}

// Default comparison to previous month
function getDefaultCompareStartDate(): string {
  const date = new Date()
  date.setMonth(date.getMonth() - 1) // Previous month
  date.setDate(1) // First day
  return formatDateYyyyMmDd(date)
}

function getDefaultCompareEndDate(): string {
  const date = new Date()
  date.setDate(0) // Last day of previous month
  return formatDateYyyyMmDd(date)
}

// Reusable panel component for a single dashboard with date range
interface DefectsDashboardPanelProps {
  startDate: string
  endDate: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  viewMode: ViewMode
}

function DefectsDashboardPanel({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  viewMode,
}: DefectsDashboardPanelProps) {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [dashboardData, setDashboardData] =
    useState<DefectsDashboardData | null>(null)
  const [aggregates, setAggregates] = useState<
    AggregateEntry<DefectTypeData>[]
  >([])
  const [groupedTotals, setGroupedTotals] = useState<{
    totalCount: number
    totalDamageCost: number
  } | null>(null)

  async function fetchDashboardData() {
    setIsLoading(true)
    try {
      if (viewMode === 'default') {
        const data = await fetchData<DefectsDashboardData>(
          getDefectsDashboardData(startDate, endDate),
        )
        setDashboardData(data)
        setAggregates([])
        setGroupedTotals(null)
      } else if (
        viewMode === 'by-technician' ||
        viewMode === 'at-fault-by-technician'
      ) {
        const data = await fetchData<DefectsByTechnicianData>(
          getDefectsDashboardByTechnician(
            viewMode === 'at-fault-by-technician',
            startDate,
            endDate,
          ),
        )
        setDashboardData(null)
        setAggregates(
          data.technicians.map((t) => ({
            key: t.technicianObjectId,
            name: t.technicianName,
            data: t.defectsByType,
            subtotals: [
              t.subtotals.totalCount,
              formatCurrency(t.subtotals.totalDamageCost),
            ],
          })),
        )
        setGroupedTotals(data.totals)
      } else if (viewMode === 'by-location') {
        const data = await fetchData<DefectsByLocationData>(
          getDefectsDashboardByLocation(startDate, endDate),
        )
        setDashboardData(null)
        setAggregates(
          data.locations.map((l) => ({
            key: l.locationObjectId,
            name: l.locationName,
            data: l.defectsByType,
            subtotals: [
              l.subtotals.totalCount,
              formatCurrency(l.subtotals.totalDamageCost),
            ],
          })),
        )
        setGroupedTotals(data.totals)
      }
    } catch (err) {
      console.error(err)
      setDashboardData(null)
      setAggregates([])
      setGroupedTotals(null)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Failed to load defects dashboard data',
        severity: 'error',
      })
    }
    setIsLoading(false)
  }

  useEffect(() => {
    fetchDashboardData()
  }, [startDate, endDate, viewMode])

  const columns: DashboardColumn<DefectTypeData>[] = useMemo(
    () => [
      {
        Header: 'Defect Type',
        accessor: 'typeDisplayName',
      },
      {
        Header: 'Count',
        accessor: 'count',
        tooltip: 'Number of Defects of this Type',
        Cell: ({ row, value }) => {
          const isHighDefectCount = (value as number) > 10
          return {
            value,
            tooltip: isHighDefectCount
              ? `High defect count for ${row.typeDisplayName}`
              : undefined,
            color: isHighDefectCount ? 'red' : undefined,
          }
        },
      },
      {
        Header: 'Total Damage Cost',
        accessor: 'totalDamageCost',
        tooltip: 'Sum of Portal and Risk Connect damage costs for this Type',
        Cell: ({ row, value }) => ({
          value: formatCurrency(value as number),
        }),
      },
    ],
    [],
  )

  // MultiLineDashboard uses the same column interface
  const multiLineColumns: MultiLineDashboardColumn<DefectTypeData>[] = columns

  const totals = useMemo(() => {
    if (!dashboardData) return undefined
    return [
      dashboardData.totals.totalCount,
      formatCurrency(dashboardData.totals.totalDamageCost),
    ]
  }, [dashboardData])

  const grandTotals = useMemo(() => {
    if (!groupedTotals) return undefined
    // First element is empty to align with the "Defect Type" column
    return [
      '', // Empty cell for Defect Type column
      groupedTotals.totalCount,
      formatCurrency(groupedTotals.totalDamageCost),
    ]
  }, [groupedTotals])

  const dateControls = (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        alignItems: 'center',
      }}
    >
      <TextField
        label='Start Date'
        type='date'
        value={startDate}
        InputLabelProps={{ shrink: true }}
        onChange={(e) => onStartDateChange(e.target.value)}
        disabled={isLoading}
        size='small'
      />
      <TextField
        label='End Date'
        type='date'
        value={endDate}
        InputLabelProps={{ shrink: true }}
        onChange={(e) => onEndDateChange(e.target.value)}
        disabled={isLoading}
        size='small'
      />
    </Box>
  )

  // Grouped view (by technician or by location) using MultiLineDashboard
  if (viewMode !== 'default') {
    const aggregateColumnHeader =
      viewMode === 'by-technician' || viewMode === 'at-fault-by-technician'
        ? 'Technician'
        : 'Location'

    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {dateControls}
        <MultiLineDashboard<DefectTypeData>
          columns={multiLineColumns}
          aggregates={aggregates}
          aggregateColumnHeader={aggregateColumnHeader}
          isLoading={isLoading}
          totals={grandTotals}
          totalsLabel='Grand Total'
          subtotalsLabel='Subtotal'
          getRowKey={(row, aggregateKey) => `${aggregateKey}-${row.typeKey}`}
          noDataMessage='No defects data available.'
        />
      </Box>
    )
  }

  // Default view
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {dateControls}
      <Dashboard<DefectTypeData>
        columns={columns}
        data={dashboardData?.defectsByType ?? []}
        isLoading={isLoading}
        totals={totals}
        totalsLabel='Total'
        getRowKey={(row) => row.typeKey}
        noDataMessage='No defects data available.'
      />
    </Box>
  )
}

function DefectsDashboard() {
  const [compareMode, setCompareMode] = useState<boolean>(false)
  const [viewMode, setViewMode] = useState<ViewMode>('default')

  // Primary date range state
  const [startDate, setStartDate] = useState<string>(getDefaultStartDate())
  const [endDate, setEndDate] = useState<string>(getDefaultEndDate())

  // Comparison date range state
  const [compareStartDate, setCompareStartDate] = useState<string>(
    getDefaultCompareStartDate(),
  )
  const [compareEndDate, setCompareEndDate] = useState<string>(
    getDefaultCompareEndDate(),
  )

  function handleByTechnicianChange(_: unknown, checked: boolean) {
    if (checked) {
      setViewMode('by-technician')
    } else {
      setViewMode('default')
    }
  }

  function handleByLocationChange(_: unknown, checked: boolean) {
    if (checked) {
      setViewMode('by-location')
    } else {
      setViewMode('default')
    }
  }

  function handleAtFaultByTechnicianChange(_: unknown, checked: boolean) {
    if (checked) {
      setViewMode('at-fault-by-technician')
    } else {
      setViewMode('default')
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <PrimaryCheckbox
          checked={compareMode}
          label='Compare'
          onChange={(_, checked) => setCompareMode(checked)}
        />
        <PrimaryCheckbox
          checked={viewMode === 'by-technician'}
          label='By Technician'
          onChange={handleByTechnicianChange}
        />
        <PrimaryCheckbox
          checked={viewMode === 'by-location'}
          label='By Location'
          onChange={handleByLocationChange}
        />
        <PrimaryCheckbox
          checked={viewMode === 'at-fault-by-technician'}
          label='At Fault - By Technician'
          onChange={handleAtFaultByTechnicianChange}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexDirection: compareMode ? 'row' : 'column',
        }}
      >
        <DefectsDashboardPanel
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          viewMode={viewMode}
        />
        {compareMode && (
          <DefectsDashboardPanel
            startDate={compareStartDate}
            endDate={compareEndDate}
            onStartDateChange={setCompareStartDate}
            onEndDateChange={setCompareEndDate}
            viewMode={viewMode}
          />
        )}
      </Box>
    </>
  )
}

export default DefectsDashboard
